<template>
  <el-popover
    placement="bottom-end"
    :width="popoverWidth"
    trigger="click"
    :offset="0"
  >
    <el-input
      v-model="searchValue"
      class="mb-2"
      :placeholder="$t('common.inputs.placeholders.search')"
      size="default"
      @keyup="searchFilters"
    >
      <template #prefix>
        <search-icon class="w-3.5 mr-2" />
      </template>
    </el-input>

    <div class="min-h-[120px] max-h-[288px] overflow-auto">
      <div
        v-for="(header, index) in searchedFilter"
        :key="`${index}-${header.label}`"
        data-testid="header-check"
        class="cursor-pointer"
        @click="triggerChange(header)"
      >
        <el-checkbox
          v-model="header.enabled"
          :disabled="checkIfCheckboxDisabled(header)"
          size="default"
          :label="header.label"
          @click.prevent
        />
      </div>
    </div>

    <footer
      class="-mx-3 px-3 pt-2 bg-white z-[1] border-t border-th-border-light"
    >
      <el-button
        type="primary"
        class="h-[--el-component-size]"
        link
        @click="restoreDefaults()"
      >
        {{ $t('components.table_filter.restore') }}
      </el-button>
    </footer>

    <template #reference>
      <el-button
        class="absolute inset-0"
        text
        size="large"
        type="info"
        :icon="CirclePlusIcon"
      />
    </template>
  </el-popover>
</template>

<script>
import { isUnifiedCommerce } from '@shared/constants'
import { SearchIcon, CirclePlusIcon } from '@shared/assets/icons'

export default {
  components: {
    SearchIcon
  },
  props: {
    headers: {
      type: Array,
      required: true
    },
    initialValues: {
      type: Object,
      required: false,
      default: () => ({})
    },
    headersReset: {
      type: Array,
      required: false,
      default: () => []
    },
    headersDefaultHide: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data() {
    return {
      filter: [],
      searchValue: '',
      searchedFilter: null,
      CirclePlusIcon
    }
  },
  computed: {
    popoverWidth() {
      return isUnifiedCommerce() ? 255 : 240
    }
  },
  watch: {
    headers: {
      deep: true,
      handler() {
        this.setFilterValue(this.initialValues)
        this.searchFilters()
      }
    },
    initialValues: {
      deep: true,
      handler(newInitialValues) {
        this.setFilterValue(newInitialValues)
        this.searchFilters()
      }
    }
  },
  created() {
    document.addEventListener('click', this.closeOnOutsideClick)
  },
  mounted() {
    this.setFilterValue(this.initialValues)
    this.searchedFilter = this.filter
    this.restoreIfDefaultsAreEmpty(this.initialValues)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeOnOutsideClick)
  },
  methods: {
    toggleHeaderVisibility() {
      this.showHeaderFilterList = !this.showHeaderFilterList
    },
    triggerChange(header) {
      const visibleHeaders = this.filter.filter(
        (value) => value.enabled === true
      )
      if (
        visibleHeaders.length !== 1 ||
        visibleHeaders[0].field !== header.field
      ) {
        const filterHeader = this.filter.find((h) => h.label === header.label)
        filterHeader.enabled = !filterHeader.enabled
        this.$emit(
          'update:modelValue',
          this.filter.filter((item) => item.enabled === true)
        )
      }
    },
    closeOnOutsideClick(e) {
      if (this.$el.contains && !this.$el.contains(e.target)) {
        this.showHeaderFilterList = false
      }
    },
    searchFilters() {
      if (this.searchValue !== '') {
        const search = this.searchValue
        const matchingObjects = this.filter.filter((obj) => {
          if (obj.label && typeof obj.label === 'string') {
            return obj.label.toLowerCase().includes(search.toLowerCase())
          }
          return false
        })

        this.searchedFilter = matchingObjects
      } else {
        this.searchedFilter = this.filter
      }
    },
    restoreIfDefaultsAreEmpty(headerSettings) {
      const hasDefault = Object.values(headerSettings).some(
        (value) => value === true
      )

      if (!hasDefault) {
        const currentyActiveReset = this.filter
          .map((item) => ({
            ...item,
            enabled: this.initialValues[item.field] ?? item.enabled
          }))
          .filter(({ enabled }) => enabled)

        if (currentyActiveReset.length > 0) {
          this.searchedFilter = this.filter
          this.$emit('update:modelValue', currentyActiveReset)
        } else {
          // No active headers after restoring from config. Restore defaults.
          this.restoreDefaults()
        }
      }
    },
    restoreDefaults() {
      this.searchValue = ''
      const headers = this.headersReset?.length
        ? this.headersReset
        : this.headers
            .filter(
              ({ field }) => field && !this.headersDefaultHide.includes(field)
            )
            .map((item) => ({ ...item, enabled: true }))
      this.searchedFilter = headers
      this.$emit('update:modelValue', headers)
    },
    checkIfCheckboxDisabled(header) {
      const onlyOneHeaderVisible =
        Object.values(this.initialValues).filter((value) => value === true)
          .length === 1
      return header.enabled && onlyOneHeaderVisible
    },
    setFilterValue(prop) {
      this.filter = this.headers.map((item) => {
        return {
          ...item,
          enabled: prop[item.field] === undefined ? true : prop[item.field]
        }
      })
    }
  }
}
</script>

<style scoped>
.header-title {
  font-family: var(--font-face);
  font-size: var(--control-font-size);
  line-height: var(--control-line-height);
  font-weight: var(--control-font-weight);
  letter-spacing: var(--letter-spacing-titles);
  color: var(--el-table-text-color-control);
}

.restore-filters {
  position: sticky;
  bottom: -12px;
  left: 0;
  border-top: 1px solid var(--disabled-color);
  padding: 16px 8px;
  color: var(--primary-color);
  text-transform: uppercase;
  font-weight: var(--button-font-weight);
  font-size: var(--button-font-size);
  letter-spacing: var(--letter-spacing-table);
  cursor: pointer;
  background: white;
  width: 100%;
  z-index: 1;
}
</style>
